'use client'

import AdCardWrapper from '@/components/_cards/ad/ad-card-wrapper'
import EventCard from '@/components/_cards/event/event-card'
import Pager from '@/components/search/pager'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useEffect, useRef, useTransition } from 'react'
import { combineEventsWithAds } from './racing-event-search-helpers'

// Matches screen-grid's padding, 10/12-column inset and the result card gaps.
const COVER_SIZES =
  '(min-width: 1920px) 435px, (min-width: 1536px) calc(27.778vw - 74.667px), (min-width: 1280px) calc(27.778vw - 61.111px), (min-width: 1024px) calc(50vw - 60px), (min-width: 768px) calc(50vw - 36px), calc(100vw - 50px)'

export default function RacingEventSearch({
  searchParams,
  sessionUserId,
  initialResults = {},
  initialAds = [],
  initialAttendanceByEventId = {},
}) {
  const pathname = usePathname()
  const router = useRouter()
  const [isSearching, startTransition] = useTransition()
  const results = initialResults || {}
  const ads = initialAds
  const pageNum = results.page || 1
  const resultsRef = useRef(null)
  const pendingPage = useRef(null)
  useEffect(() => {
    if (pendingPage.current === pageNum) {
      pendingPage.current = null
      resultsRef.current?.focus({ preventScroll: true })
      resultsRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'instant',
      })
    }
  }, [pageNum])
  const handlePageChange = (page) => {
    if (isSearching || page === pageNum) return
    pendingPage.current = page
    const params = new URLSearchParams(searchParams)
    params.delete('ts')
    params.set('page', String(page))
    startTransition(() =>
      router.push(`${pathname}?${params}`, { scroll: false }),
    )
  }
  return (
    <>
      <div
        ref={resultsRef}
        tabIndex={-1}
        aria-busy={isSearching}
        role="region"
        aria-label="Race results"
        className="screen-grid col-span-full mt-6 scroll-mt-24 focus:outline-none"
      >
        <div className="col-span-full grid min-w-0 grid-cols-1 gap-5 tablet:grid-cols-2 desktop-sm:col-span-10 desktop-sm:col-start-2 desktop-sm:grid-cols-3 desktop-lg:gap-9">
          {isSearching && (
            <p role="status" className="col-span-full">
              Updating results…
            </p>
          )}
          {results.events && results.events?.length === 0 ? (
            <div className="col-span-full rounded-lg border border-neutral-blue-700 p-6">
              <h2 className="text-lg font-semibold">
                No races match these filters
              </h2>
              <p className="mt-2 text-neutral-blue-200">
                Try fewer filters or a different keyword. To start again, select
                Clear all filters, then Search.
              </p>
              <Link
                href="/sim-racing-communities"
                className="mt-2 font-inter text-p-2 font-semibold text-neutral-blue-200 underline underline-offset-2"
              >
                Looking to search communities instead?
              </Link>
            </div>
          ) : (
            combineEventsWithAds(results.events, ads).map((item) => (
              <div key={item.key} className="min-w-0">
                {item.type === 'event' ? (
                  <EventCard
                    championship={item.data.championship}
                    event={item.data}
                    initialAttendance={
                      initialAttendanceByEventId?.[item.data.id]
                    }
                    imageSizes={COVER_SIZES}
                    disablePrefetch
                    lazyAttendance
                    showRegistrationCountdownOnly
                    session={
                      sessionUserId ? { user: { id: sessionUserId } } : null
                    }
                  />
                ) : (
                  <AdCardWrapper ad={item.data} placement={item.placement} />
                )}
              </div>
            ))
          )}
        </div>

        {!isSearching && results.events?.length > 0 && (
          <Pager
            className="col-span-full mt-10 desktop-sm:col-span-10 desktop-sm:col-start-2 desktop-sm:max-w-[408px] desktop-sm:justify-self-end"
            page={pageNum}
            pageLimit={Math.ceil(results.total / results.pageSize)}
            setPage={handlePageChange}
          />
        )}
      </div>
    </>
  )
}
